"use client"

import type React from "react"

import { useState, useEffect } from "react"
import { useRouter, useSearchParams } from "next/navigation"
import { supabase } from "@/lib/supabase"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import Link from "next/link"
import { Checkbox } from "@/components/ui/checkbox"
import { getEmailDomainRegisterRestriction, validateEmailDomain } from "@/lib/settings"

export default function SignupPage() {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [rememberMe, setRememberMe] = useState(true)
  const [emailDomainRegisterRestricted, setEmailDomainRegisterRestricted] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()

  useEffect(() => {
    const urlError = searchParams.get("error")
    if (urlError) {
      switch (urlError) {
        case "invalid_link":
          setError("De verificatielink is ongeldig of verlopen.")
          break
        case "verification_failed":
          setError("Verificatie mislukt. Probeer opnieuw aan te melden.")
          break
        case "verification_error":
          setError("Er is een fout opgetreden tijdens de verificatie.")
          break
        default:
          setError("Er is een onbekende fout opgetreden.")
      }
    }

    checkEmailDomainRestriction()
  }, [searchParams])

  const checkEmailDomainRestriction = async () => {
    const isRestricted = await getEmailDomainRegisterRestriction()
    setEmailDomainRegisterRestricted(isRestricted)
  }

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      if (emailDomainRegisterRestricted && !validateEmailDomain(email)) {
        setError("Alleen @procap.nl emailadressen zijn toegestaan voor aanmelding.")
        setLoading(false)
        return
      }

      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo:
            process.env.NEXT_PUBLIC_DEV_SUPABASE_REDIRECT_URL || `${window.location.origin}/auth/callback`,
        },
      })

      if (error) {
        setError(error.message)
      } else {
        setSuccess(true)
      }
    } catch (err: any) {
      setError("Er is een onverwachte fout opgetreden: " + err.message)
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl font-bold">Bevestig je e-mailadres</CardTitle>
            <CardDescription>We hebben een bevestigingslink naar {email} gestuurd.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md bg-green-50 p-4">
              <div className="text-sm text-green-700">
                <p className="font-medium">E-mail verzonden!</p>
                <p className="mt-1">
                  Klik op de link in je e-mail om je account te activeren. Het kan een paar minuten duren voor deze
                  ontvangt. Controleer ook je spam/ongewenste e-mail map.
                </p>
              </div>
            </div>
            <div className="text-center text-sm text-gray-600">
              <p>Geen e-mail ontvangen?</p>
              <Button variant="link" className="p-0 h-auto font-normal" onClick={() => setSuccess(false)}>
                Probeer opnieuw
              </Button>
            </div>
            <div className="text-center text-sm">
              <Link href="/inloggen" className="underline">
                Terug naar inloggen
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Aanmelden</CardTitle>
          <CardDescription>
            {emailDomainRegisterRestricted
              ? "Maak een nieuw account met je @procap.nl e-mailadres om te beginnen."
              : "Maak een nieuw account om te beginnen."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSignup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">E-mailadres</Label>
              <Input
                id="email"
                type="email"
                placeholder={emailDomainRegisterRestricted ? "email@procap.nl" : "email@example.com"}
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              {emailDomainRegisterRestricted && (
                <p className="text-xs text-muted-foreground">Alleen @procap.nl emailadressen zijn toegestaan</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Wachtwoord</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="rememberMe" checked={rememberMe} onCheckedChange={(checked) => setRememberMe(!!checked)} />
              <Label htmlFor="rememberMe">Onthoud mij</Label>
            </div>
            {error && <p className="text-sm text-red-500">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? "Aanmelden..." : "Aanmelden"}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            Al een account?{" "}
            <Link href="/inloggen" className="underline">
              Log in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
